import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog, faExclamationTriangle, faWrench, faMicrochip, faInfo } from '@fortawesome/free-solid-svg-icons'
import { SensorConfiguration } from '../components/SensorConfiguration'
import { SensorCalibration } from '../components/SensorCalibration'
import { useAccountContext } from '../contexts'
import { SENSOR_PORTS, SENSOR_PORT_COLORS } from '../types/sensor'
import { useSensorConfigs } from '../hooks/useSensorConfigs'

type ManagementPanel = 'config' | 'calibration'

export function SensorPage() {
  const [selectedSensorNumber, setSelectedSensorNumber] = useState<number>(0)
  const [activePanel, setActivePanel] = useState<ManagementPanel>('config')

  // Get selected device from global state (from side navigation)
  const { devices, selectedDevice: selectedDeviceId } = useAccountContext()
  const selectedDevice = selectedDeviceId ? devices[selectedDeviceId] : null

  // Fetch sensor configurations from API
  const { data: sensorConfigData } = useSensorConfigs(
    selectedDevice?.sigfoxId || '',
    undefined,
    true, // showAllSensors
    !!selectedDevice?.sigfoxId
  )

  // Helper function to get sensor configuration for a specific port
  const getSensorConfig = (sensorNumber: number) => {
    if (!sensorConfigData?.configs) return null

    // Find the sensor configuration for this sensor number
    return sensorConfigData.configs.find((config: any) => config.sensorId === sensorNumber)
  }

  const getSensorName = (sensorNumber: number): string => {
    const config = getSensorConfig(sensorNumber)
    return config?.name || `Sensor ${sensorNumber}`
  }

  const getSensorType = (sensorNumber: number): string => {
    const config = getSensorConfig(sensorNumber)
    if (!config || !config.type || !config.enabled) return 'Not configured'

    // Map sensor type codes to readable names
    const typeMap: Record<string, string> = {
      'VH400': 'VH400 Moisture',
      'Therm200': 'Therm200 Temperature',
      'AQUAPLUMB': 'AquaPlumb',
      'VP-4': 'VP-4 VPD',
      'SM150T': 'SM150T Moisture',
      'WaterMark': 'Watermark',
      'EC-5': 'EC-5 Moisture',
      'Irrometer': 'Irrometer',
      'Flow': 'Flow Meter',
      'Pressure': 'Pressure Sensor',
      'Temperature': 'Temperature',
      'Humidity': 'Humidity',
      'Rainfall': 'Rainfall',
      'WindSpeed': 'Wind Speed',
      'SolarRadiation': 'Solar Radiation'
    }

    return typeMap[config.type] || config.type
  }

  const getSensorUnit = (sensorNumber: number): string => {
    const config = getSensorConfig(sensorNumber)
    if (!config) return ''

    // Check if unit is explicitly defined in config
    if (config.unit) return config.unit

    // Return unit based on sensor type
    const unitMap: Record<string, string> = {
      'VH400': '%',
      'Therm200': '°C',
      'AQUAPLUMB': 'kPa',
      'VP-4': 'kPa',
      'SM150T': '%',
      'WaterMark': 'kPa',
      'EC-5': '%',
      'Irrometer': 'kPa',
      'Flow': 'L/h',
      'Pressure': 'kPa',
      'Temperature': '°C',
      'Humidity': '%',
      'Rainfall': 'mm',
      'WindSpeed': 'm/s',
      'SolarRadiation': 'W/m²'
    }

    return unitMap[config.type] || ''
  }

  const getPortName = (sensorNumber: number): string => {
    return SENSOR_PORTS[sensorNumber as keyof typeof SENSOR_PORTS] || `Port ${sensorNumber}`
  }

  const getPortColor = (sensorNumber: number): string => {
    return SENSOR_PORT_COLORS[sensorNumber as keyof typeof SENSOR_PORT_COLORS] || '#666666'
  }

  const handleSensorSelect = (sensorNumber: number) => {
    setSelectedSensorNumber(sensorNumber)
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Sensor Management</h1>
        <p className="mt-1 text-sm text-gray-600">Configure sensors and calibration settings for the selected device</p>
      </div>

      {!selectedDevice ? (
        // No device selected message
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="h-16 w-16 text-amber-500 mb-4 mx-auto" />
          <div className="text-lg font-medium text-amber-800 mb-2">No Device Selected</div>
          <div className="text-sm text-amber-700">
            Please select a device from the navigation menu to configure its sensors
          </div>
        </div>
      ) : (
        // Two Column Layout: Sensors + Configuration
        <div className="grid grid-cols-12 gap-6 h-[calc(100vh-12rem)]">
          {/* Column 1: Sensor List */}
          <div className="col-span-5 bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Sensors</h2>
              <div className="text-sm text-gray-600 mt-1">Device: {selectedDevice?.deviceName || selectedDeviceId}</div>
            </div>
            <div className="overflow-y-auto h-full">
              <div className="p-4 space-y-3">
                {[1, 2, 3, 4, 5, 6, 7].map((sensorNumber) => {
                  const isActive = selectedSensorNumber === sensorNumber
                  const portColor = getPortColor(sensorNumber)
                  const config = getSensorConfig(sensorNumber)
                  const sensorName = getSensorName(sensorNumber)
                  const sensorType = getSensorType(sensorNumber)
                  const sensorUnit = getSensorUnit(sensorNumber)
                  const portName = getPortName(sensorNumber)
                  const isConfigured = !!(config && config.enabled && config.type)

                  return (
                    <button
                      key={sensorNumber}
                      onClick={() => handleSensorSelect(sensorNumber)}
                      className={`w-full p-4 rounded-lg border text-left transition-colors ${
                        isActive
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div
                          className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5"
                          style={{ backgroundColor: portColor }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-medium text-gray-900">{portName}</div>
                            <FontAwesomeIcon icon={faCog} className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          </div>

                          {isConfigured ? (
                            <>
                              <div className="text-sm font-medium text-gray-800 mb-1">{sensorName}</div>
                              <div className="flex items-center space-x-1 text-xs text-gray-600">
                                <FontAwesomeIcon icon={faMicrochip} className="h-3 w-3" />
                                <span>{sensorType}</span>
                                {sensorUnit && (
                                  <>
                                    <span>•</span>
                                    <span>{sensorUnit}</span>
                                  </>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="text-sm text-gray-500 italic">Not configured</div>
                          )}
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Column 2: Configuration/Calibration Panel */}
          <div className="col-span-7 bg-white rounded-lg shadow overflow-hidden">
            {!selectedSensorNumber ? (
              <div className="p-8 text-center text-gray-500 h-full flex flex-col justify-center">
                <FontAwesomeIcon icon={faWrench} className="h-16 w-16 mb-4 mx-auto" />
                <div className="text-lg font-medium mb-2">Sensor Configuration</div>
                <div className="text-sm">
                  Select a sensor port from the list to configure its settings and calibration
                </div>
              </div>
            ) : (
              <>
                {/* Panel Header with Tabs */}
                <div className="border-b border-gray-200">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-medium text-gray-900">
                        {getPortName(selectedSensorNumber)} Configuration
                      </h2>
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: getPortColor(selectedSensorNumber) }}
                        />
                        <span>{getSensorName(selectedSensorNumber)}</span>
                      </div>
                    </div>

                    {/* Sensor Info Summary */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-700 mb-2">
                        <FontAwesomeIcon icon={faInfo} className="h-4 w-4 text-blue-500" />
                        <span className="font-medium">Sensor Information</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                        <div>
                          <span className="font-medium">Type:</span> {getSensorType(selectedSensorNumber)}
                        </div>
                        <div>
                          <span className="font-medium">Unit:</span> {getSensorUnit(selectedSensorNumber) || 'Not set'}
                        </div>
                        <div>
                          <span className="font-medium">Port:</span> {getPortName(selectedSensorNumber)}
                        </div>
                        <div>
                          <span className="font-medium">Status:</span>{' '}
                          <span
                            className={
                              getSensorType(selectedSensorNumber) !== 'Not configured'
                                ? 'text-green-600'
                                : 'text-amber-600'
                            }
                          >
                            {getSensorType(selectedSensorNumber) !== 'Not configured'
                              ? 'Configured'
                              : 'Not configured'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <nav className="flex space-x-4">
                      <button
                        onClick={() => setActivePanel('config')}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activePanel === 'config'
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <FontAwesomeIcon icon={faCog} className="mr-2 h-4 w-4" />
                        Configuration
                      </button>
                      <button
                        onClick={() => setActivePanel('calibration')}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          activePanel === 'calibration'
                            ? 'bg-blue-100 text-blue-700'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <FontAwesomeIcon icon={faWrench} className="mr-2 h-4 w-4" />
                        Calibration
                      </button>
                    </nav>
                  </div>
                </div>

                {/* Panel Content */}
                <div className="overflow-y-auto h-full">
                  {activePanel === 'config' ? (
                    <SensorConfiguration
                      selectedDevice={selectedDevice}
                      selectedSensorNumber={selectedSensorNumber}
                      onSensorSelect={() => {}}
                    />
                  ) : (
                    <SensorCalibration
                      selectedDevice={selectedDevice}
                      selectedSensorNumber={selectedSensorNumber}
                      onSensorSelect={() => {}}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SensorPage
